import { DocumentsRecord, TaxFormType } from "../types/documents.interface";

export const is1099Document = (documentType?: any): boolean => {
  return [
    TaxFormType["1042S"],
    TaxFormType["1099B"],
    TaxFormType["1099DA"],
    TaxFormType["1099DIV"],
    TaxFormType["1099INT"],
    TaxFormType["1099MISC"],
    TaxFormType["1099K"],
  ].includes(documentType as TaxFormType);
};

export const is1099BDocument = (documentType?: any): boolean => {
  return [TaxFormType["1099B"], TaxFormType["1099DA"]].includes(documentType as TaxFormType);
};

export const isOtherDocument = (documentType?: any): boolean => {
  return [TaxFormType.OTHER].includes(documentType as TaxFormType);
};

export const isTaxDocument = (documentType?: any): boolean => {
  return [TaxFormType.W9, TaxFormType.W8BEN, TaxFormType.W8BENE].includes(
    documentType as TaxFormType,
  );
};

export const isRenewableTaxDocument = (documentType?: any): boolean => {
  return [TaxFormType.W8BEN, TaxFormType.W8BENE].includes(
    documentType as TaxFormType,
  );
};

export const getTaxFormLabel = (formType: TaxFormType): string =>
  formType === TaxFormType.W8BENE ? "W-8BEN-E" : formType;

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * Compared as plain strings
 */
const EXPIRED_STATUS = "expired";
const VALID_STATUS = "valid";
const PENDING_STATUS = "pending";
const INVALID_STATUS = "invalid";

export type TaxCenterAlertState =
  | "no-form"
  | "under-review"
  | "invalid"
  | "expiring-soon"
  | "expired"
  | "none";

export const getRenewableForm = (
  documents: DocumentsRecord[],
): DocumentsRecord | undefined =>
  documents.find((doc) => isRenewableTaxDocument(doc.documentType));

const isActiveTaxForm = (doc: DocumentsRecord): boolean =>
  doc.status === VALID_STATUS || doc.status === PENDING_STATUS;

/**
 * The active W-9/W-8 if there is one, otherwise the most recently submitted
 */
export const getCurrentTaxForm = (
  documents: DocumentsRecord[],
): DocumentsRecord | undefined => {
  const taxForms = documents.filter((doc) => isTaxDocument(doc.documentType));
  const activeForms = taxForms.filter(isActiveTaxForm);
  const candidates = activeForms.length ? activeForms : taxForms;
  return candidates.reduce<DocumentsRecord | undefined>(
    (latest, doc) =>
      !latest ||
      (doc.submissionDate?.getTime() ?? 0) >
        (latest.submissionDate?.getTime() ?? 0)
        ? doc
        : latest,
    undefined,
  );
};

export const getWithholdingRate = (formType: TaxFormType): string =>
  formType === TaxFormType.W9 ? "24%" : "30%";

export const getDaysUntilExpiration = (expirationDate: Date): number =>
  Math.ceil((expirationDate.getTime() - Date.now()) / MS_PER_DAY);

export const getTaxCenterAlertState = (
  documents: DocumentsRecord[],
  isExpiringSoon: boolean,
): TaxCenterAlertState => {
  if (!documents.some((doc) => isTaxDocument(doc.documentType))) {
    return "no-form";
  }

  const currentForm = getCurrentTaxForm(documents);
  if (currentForm?.status === PENDING_STATUS) return "under-review";
  if (currentForm?.status === INVALID_STATUS) return "invalid";

  const renewableForm = getRenewableForm(documents);
  if (!renewableForm) return "none";

  if (renewableForm.status === EXPIRED_STATUS) {
    return "expired";
  }

  return isExpiringSoon ? "expiring-soon" : "none";
};

/**
 * W9 form is only for US persons and US entities
 * W8BEN form is only for non-US persons
 * W8BENE form is only for non-US entities
 * Returns undefined when the investor's tax profile doesn't resolve to
 * exactly one of those (e.g. has both US and foreign tax) — the caller
 * should fall back to manual selection / generic copy in that case.
 */
export const resolveRequiredFormType = (
  hasUSTax: boolean,
  hasForeignTax: boolean,
  isEntity: boolean,
): TaxFormType | undefined => {
  if (hasUSTax && !hasForeignTax) {
    return TaxFormType.W9;
  }
  if (!isEntity && hasForeignTax && !hasUSTax) {
    return TaxFormType.W8BEN;
  }
  if (isEntity && hasForeignTax && !hasUSTax) {
    return TaxFormType.W8BENE;
  }
  return undefined;
};
